import React, { useEffect, useState } from "react";
import { ImagePlus, Loader2, RotateCcw, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import api, { formatApiError, resolveApiResourceUrl } from "../../lib/api";
import { PHOTO_ACCEPT, validatePhotoFile } from "../../lib/photoValidation";
import { Button, Card, CardBody, Input, PageHeader, Textarea } from "../../components/admin/UI";

const EDITABLE_KEYS = ["revision", "hero", "about", "sections", "closing_cta"];

function editable(data) {
    return Object.fromEntries(EDITABLE_KEYS.map((key) => [key, data[key]]));
}

function updatePayload(form) {
    const hero = { ...form.hero };
    const about = { ...form.about };
    delete hero.has_image;
    delete hero.image_url;
    delete about.has_image;
    delete about.image_url;
    return { revision: form.revision, hero, about, sections: form.sections, closing_cta: form.closing_cta };
}

function Toggle({ label, checked, onChange, testId }) {
    return (
        <label className="flex items-center justify-between gap-4 rounded-lg border border-slate-200 p-3">
            <span className="text-sm font-medium text-slate-800">{label}</span>
            <input
                data-testid={testId}
                type="checkbox"
                checked={checked}
                onChange={(event) => onChange(event.target.checked)}
                className="h-5 w-5 accent-orange-500"
            />
        </label>
    );
}

function CountedInput({ label, value, limit, multiline = false, ...props }) {
    const Field = multiline ? Textarea : Input;
    return (
        <div>
            <Field label={label} value={value} maxLength={limit} {...props} />
            <div className="mt-1 text-right text-xs text-slate-400">{value.length}/{limit}</div>
        </div>
    );
}

function ImageEditor({ slot, label, imageUrl, hasImage, busy, onUpload, onDelete }) {
    return (
        <div className="space-y-3">
            <div className="overflow-hidden rounded-xl border border-slate-200 bg-slate-100 aspect-video">
                {imageUrl ? (
                    <img src={resolveApiResourceUrl(imageUrl)} alt={label} className="h-full w-full object-cover" />
                ) : (
                    <div className="flex h-full items-center justify-center text-sm text-slate-500">Aucune image personnalisée</div>
                )}
            </div>
            <div className="flex flex-wrap gap-2">
                <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-900 hover:bg-slate-200">
                    <ImagePlus className="h-4 w-4" /> {hasImage ? "Remplacer" : "Ajouter"}
                    <input
                        data-testid={`${slot}-image-input`}
                        type="file"
                        accept={PHOTO_ACCEPT}
                        className="hidden"
                        disabled={busy}
                        onChange={(event) => {
                            const file = event.target.files?.[0];
                            event.target.value = "";
                            if (file) onUpload(file);
                        }}
                    />
                </label>
                {hasImage && (
                    <Button data-testid={`${slot}-image-delete`} type="button" variant="danger" disabled={busy} onClick={onDelete}>
                        <Trash2 className="h-4 w-4" /> Supprimer
                    </Button>
                )}
            </div>
            <p className="text-xs text-slate-500">
                JPEG, PNG ou WebP · minimum 300 × 300 px. Le cadrage est automatique.
            </p>
        </div>
    );
}

export default function Homepage() {
    const { activeClubId } = useAuth();
    const [form, setForm] = useState(null);
    const [defaults, setDefaults] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [imageBusy, setImageBusy] = useState(false);

    const load = async () => {
        if (!activeClubId) return;
        setLoading(true);
        try {
            const { data } = await api.get(`/clubs/${activeClubId}/homepage`);
            setForm(editable(data));
            setDefaults(data.defaults);
        } catch (error) {
            toast.error(formatApiError(error));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [activeClubId]); // eslint-disable-line react-hooks/exhaustive-deps

    const setSection = (section, key, value) => {
        setForm((current) => ({ ...current, [section]: { ...current[section], [key]: value } }));
    };

    const save = async () => {
        setSaving(true);
        try {
            const { data } = await api.put(`/clubs/${activeClubId}/homepage`, updatePayload(form));
            setForm(editable(data));
            setDefaults(data.defaults);
            toast.success("Page d’accueil publiée.");
        } catch (error) {
            if (error?.response?.status === 409) {
                toast.error("Cette page a été modifiée ailleurs. Rechargez-la avant de recommencer.");
            } else {
                toast.error(formatApiError(error));
            }
        } finally {
            setSaving(false);
        }
    };

    const upload = async (slot, file) => {
        const validationError = validatePhotoFile(file);
        if (validationError) return toast.error(validationError);
        setImageBusy(true);
        const body = new FormData();
        body.append("file", file);
        body.append("revision", String(form.revision));
        try {
            const { data } = await api.put(`/clubs/${activeClubId}/homepage/images/${slot}`, body);
            setForm((current) => ({
                ...current,
                revision: data.revision,
                [slot]: { ...current[slot], has_image: data[slot].has_image, image_url: data[slot].image_url },
            }));
            setDefaults(data.defaults);
            toast.success("Image publiée.");
        } catch (error) {
            if (error?.response?.status === 409) toast.error("Conflit de modification. Rechargez la page.");
            else toast.error(formatApiError(error));
        } finally {
            setImageBusy(false);
        }
    };

    const removeImage = async (slot) => {
        if (!window.confirm("Supprimer cette image personnalisée et rétablir l’image par défaut ?")) return;
        setImageBusy(true);
        try {
            const { data } = await api.delete(`/clubs/${activeClubId}/homepage/images/${slot}`, { params: { revision: form.revision } });
            setForm((current) => ({
                ...current,
                revision: data.revision,
                [slot]: { ...current[slot], has_image: data[slot].has_image, image_url: data[slot].image_url },
            }));
            setDefaults(data.defaults);
            toast.success("Image supprimée.");
        } catch (error) {
            if (error?.response?.status === 409) toast.error("Conflit de modification. Rechargez la page.");
            else toast.error(formatApiError(error));
        } finally {
            setImageBusy(false);
        }
    };

    const reset = () => {
        if (!window.confirm("Rétablir tous les textes et affichages par défaut ? Les images personnalisées seront conservées jusqu’à leur suppression séparée.")) return;
        setForm((current) => ({
            ...editable(defaults),
            revision: current.revision,
            hero: { ...defaults.hero, has_image: current.hero.has_image, image_url: current.hero.image_url },
            about: { ...defaults.about, has_image: current.about.has_image, image_url: current.about.image_url },
        }));
        toast.info("Valeurs par défaut chargées. Publiez pour les appliquer.");
    };

    if (loading) return <div className="flex min-h-64 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-orange-500" /></div>;
    if (!form) return <p className="text-sm text-slate-600">Impossible de charger la page d’accueil.</p>;

    return (
        <div data-testid="homepage-editor" className="space-y-6">
            <PageHeader title="Page d’accueil" subtitle="Modifiez puis publiez le contenu visible sur le site public." />

            <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(320px,0.8fr)]">
                <div className="space-y-6">
                    <Card><CardBody className="space-y-4">
                        <h2 className="font-display text-xl font-bold">Hero</h2>
                        <ImageEditor slot="hero" label="Aperçu du hero" imageUrl={form.hero.image_url} hasImage={form.hero.has_image} busy={imageBusy} onUpload={(file) => upload("hero", file)} onDelete={() => removeImage("hero")} />
                        <CountedInput label="Titre" value={form.hero.title} limit={120} onChange={(e) => setSection("hero", "title", e.target.value)} />
                        <CountedInput multiline rows={4} label="Sous-titre / texte" value={form.hero.subtitle} limit={500} onChange={(e) => setSection("hero", "subtitle", e.target.value)} />
                        <div className="grid gap-3 sm:grid-cols-2">
                            <div className="space-y-3"><Toggle label="CTA principal visible" checked={form.hero.primary_cta_visible} onChange={(v) => setSection("hero", "primary_cta_visible", v)} /><CountedInput label="Texte du bouton" value={form.hero.primary_cta_text} limit={40} onChange={(e) => setSection("hero", "primary_cta_text", e.target.value)} /></div>
                            <div className="space-y-3"><Toggle label="CTA secondaire visible" checked={form.hero.secondary_cta_visible} onChange={(v) => setSection("hero", "secondary_cta_visible", v)} /><CountedInput label="Texte du bouton" value={form.hero.secondary_cta_text} limit={40} onChange={(e) => setSection("hero", "secondary_cta_text", e.target.value)} /></div>
                        </div>
                    </CardBody></Card>

                    <Card><CardBody className="space-y-4">
                        <h2 className="font-display text-xl font-bold">À propos</h2>
                        <Toggle label="Afficher la section" checked={form.about.visible} onChange={(v) => setSection("about", "visible", v)} testId="about-visible" />
                        <ImageEditor slot="about" label="Aperçu À propos" imageUrl={form.about.image_url} hasImage={form.about.has_image} busy={imageBusy} onUpload={(file) => upload("about", file)} onDelete={() => removeImage("about")} />
                        <CountedInput label="Titre" value={form.about.title} limit={140} onChange={(e) => setSection("about", "title", e.target.value)} />
                        <CountedInput multiline rows={7} label="Description" value={form.about.text} limit={2000} onChange={(e) => setSection("about", "text", e.target.value)} />
                    </CardBody></Card>

                    <Card><CardBody className="space-y-3">
                        <h2 className="font-display text-xl font-bold">Visibilité des sections</h2>
                        <Toggle label="Valeurs" checked={form.sections.values_visible} onChange={(v) => setSection("sections", "values_visible", v)} testId="values-visible" />
                        <Toggle label="Dernières actualités" checked={form.sections.latest_news_visible} onChange={(v) => setSection("sections", "latest_news_visible", v)} testId="news-visible" />
                        <Toggle label="Prochains matchs" checked={form.sections.upcoming_matches_visible} onChange={(v) => setSection("sections", "upcoming_matches_visible", v)} testId="matches-visible" />
                        <Toggle label="CTA final" checked={form.sections.closing_cta_visible} onChange={(v) => setSection("sections", "closing_cta_visible", v)} testId="closing-visible" />
                    </CardBody></Card>

                    <Card><CardBody className="space-y-4">
                        <h2 className="font-display text-xl font-bold">CTA final</h2>
                        <CountedInput label="Titre" value={form.closing_cta.title} limit={140} onChange={(e) => setSection("closing_cta", "title", e.target.value)} />
                        <CountedInput multiline rows={4} label="Texte" value={form.closing_cta.text} limit={500} onChange={(e) => setSection("closing_cta", "text", e.target.value)} />
                        <CountedInput label="Texte du bouton" value={form.closing_cta.button_text} limit={40} onChange={(e) => setSection("closing_cta", "button_text", e.target.value)} />
                    </CardBody></Card>
                </div>

                <aside className="xl:sticky xl:top-6 xl:self-start">
                    <Card><CardBody className="space-y-4">
                        <h2 className="font-display text-xl font-bold">Aperçu</h2>
                        <div className="overflow-hidden rounded-xl bg-slate-900 text-white">
                            {form.hero.image_url && <img src={resolveApiResourceUrl(form.hero.image_url)} alt="" className="h-44 w-full object-cover opacity-70" />}
                            <div className="space-y-3 p-6"><h3 className="text-2xl font-black">{form.hero.title}</h3><p className="text-sm text-slate-200">{form.hero.subtitle}</p></div>
                        </div>
                        {form.about.visible && <div><h3 className="font-bold">{form.about.title}</h3><p className="mt-1 whitespace-pre-line text-sm text-slate-600">{form.about.text}</p></div>}
                    </CardBody></Card>
                </aside>
            </div>

            <div className="sticky bottom-3 z-10 flex flex-col-reverse gap-3 rounded-xl border border-slate-200 bg-white/95 p-4 shadow-lg backdrop-blur sm:flex-row sm:justify-between">
                <Button data-testid="homepage-reset" type="button" variant="outline" onClick={reset}><RotateCcw className="h-4 w-4" /> Rétablir les valeurs par défaut</Button>
                <Button data-testid="homepage-save" type="button" disabled={saving || imageBusy} onClick={save}>{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Enregistrer et publier</Button>
            </div>
        </div>
    );
}
